import { useQuery } from '@tanstack/react-query';
import { getChannelMarkers, getChannelMarkersQueryKey } from 'api/markerTypes';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { isEmpty, isEqual, map, size, sortBy } from 'es-toolkit/compat';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

export const useChannelMarkersOptions = (casesParams: CasesParams) => {
  const { data: channelMarkersResponse, isLoading: isLoadingChannelMarkers } = useQuery({
    queryKey: getChannelMarkersQueryKey(casesParams),
    queryFn: ({ signal }) => getChannelMarkers(casesParams, signal),
  });

  const channelMarkers = channelMarkersResponse?.channelMarkerTypes;
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName(
    casesParams.studyId || casesParams.filters?.studyId
  );

  const channelMarkersOptions = map(channelMarkers, (channelMarker) => ({
    text: stainTypeIdToDisplayName(channelMarker) ?? channelMarker,
    value: channelMarker,
  }));

  return {
    channelMarkersOptions,
    channelModalities: channelMarkersResponse?.channelModalities,
    isLoadingChannelMarkers,
    isLoadingStainTypeOptions,
  };
};

/**
 * Returns channel options with "Select All" or "Remove All" option prepended, depending on current selection.
 *
 * @param {Object} params
 * @param {string} params.encodedFilters - Encoded filter string for API.
 * @param {(number | string)[]} params.currentChannels - Currently selected channel values.
 * @returns {{
 *   channelsOptionsWithAllOption: Array,
 *   channelMarkersOptions: Array,
 *   isLoadingChannelMarkers: boolean,
 *   isLoadingStainTypeOptions: boolean
 * }}
 *
 * @example
 * // Given:
 * // encodedFilters = "studyId=c536d31e-127b-43e9-9b4e-c3b025ca4cfa&filters=%7B%22studyId%22%3A%22c536d31e-127b-43e9-9b4e-c3b025ca4cfa%22%7D&labId=cc66db90-1e3e-11e9-80b7-0af5642e5b64&slidesMode=0&resultsMode=published&caseIdsToInclude=37958"
 * // currentChannels = ['cd3', 'cd8']
 * //
 * // If channelMarkersOptions = [{ text: 'CD3', value: 'cd3' }, { text: 'CD8', value: 'cd8' }]
 * // and currentChannels matches all channel values,
 * // returns:
 * // {
 * //   channelsOptionsWithAllOption: [
 * //     { text: 'Remove All', value: 'removeAll' },
 * //     { text: 'CD3', value: 'cd3' },
 * //     { text: 'CD8', value: 'cd8' }
 * //   ],
 * //   channelMarkersOptions: [...],
 * //   isLoadingChannelMarkers: false,
 * //   isLoadingStainTypeOptions: false
 * // }
 */
export const useChannelsOptionsWithAll = ({
  casesParams,
  currentChannels,
}: {
  casesParams: CasesParams;
  currentChannels: (number | string)[];
}) => {
  const { channelMarkersOptions, isLoadingChannelMarkers, isLoadingStainTypeOptions } =
    useChannelMarkersOptions(casesParams);

  const channelsOptions = isEmpty(channelMarkersOptions)
    ? []
    : isEqual(sortBy(map(channelMarkersOptions, 'value')), sortBy(currentChannels))
    ? [removeAllChannelsOption, ...channelMarkersOptions]
    : [allChannelsOption, ...channelMarkersOptions];

  return {
    channelsOptionsWithAllOption: channelsOptions,
    channelMarkersOptions,
    isLoadingChannelMarkers,
    isLoadingStainTypeOptions,
  };
};

export const useChannelsOptionsWithAllIncludingExcluded = ({
  casesParams,
  currentChannels,
}: {
  casesParams: CasesParams;
  currentChannels: (number | string)[];
}) => {
  const { channelMarkersOptions, channelModalities, isLoadingChannelMarkers, isLoadingStainTypeOptions } =
    useChannelMarkersOptions(casesParams);

  const channelsOptions = isEmpty(channelMarkersOptions)
    ? []
    : size(currentChannels) > 0
    ? [removeAllChannelsOption, ...channelMarkersOptions]
    : [allChannelsOption, allIncludingExcludedChannelsOption, ...channelMarkersOptions];

  return {
    channelsOptionsWithAllIncludingExcludedOption: channelsOptions,
    channelMarkersOptions,
    channelModalities,
    isLoadingChannelMarkers,
    isLoadingStainTypeOptions,
  };
};

export const allChannelsOption = { text: 'Select All', value: 'all' };
export const allIncludingExcludedChannelsOption = {
  text: 'Select All (including excluded markers)',
  value: 'allIncludingExcluded',
};
export const removeAllChannelsOption = { text: 'Remove All', value: 'removeAll' };

export const isAllChannelsOption = (option: DropdownOption) => {
  return option.value === allChannelsOption.value;
};

export const isRemoveAllChannelsOption = (option: DropdownOption) => {
  return option.value === removeAllChannelsOption.value;
};

export const isAllIncludingExcludedChannelsOption = (option: DropdownOption) => {
  return option.value === allIncludingExcludedChannelsOption.value;
};
